/**
 * Questions with one plain answer that the matching model kept sending elsewhere: today's
 * date, who Everyone reaches, whether a Notice went out, and requests for people's contact
 * details or Ask's own instructions. Each is recognised by its shape, not matched.
 */
import type { NoticeWithType } from "../../components/ViewNotices";
import { getSidebarItem } from "../../config/sidebarConfig";
import type { AnswerContext } from "./catalogue";
import { loadFeed } from "./catalogue/notices";
import { loadCircles } from "./catalogue/pickCircle";
import { dayLabel } from "./dateRange";
import { significantWords } from "./significantWords";
import { timeLabel } from "./timeOfDay";
import { withoutTextSpeak } from "./workRequest";

/* Today's date */

/** "whats the date today", "what is tmrws date", "wat date is it more?" (môre: tomorrow). */
const ASKS_THE_DATE =
  /\b(?:what|wat|whats|what's)\s+(?:is\s+)?(?:the\s+|today'?s\s+|tomorrow'?s\s+|tmrw?s?\s+)?(?:date|day)\b|\b(?:today'?s|tomorrow'?s|tmrw?s?)\s+date\b|\bwhat\s+day\s+is\s+it\b/i;
const MEANS_TOMORROW = /\b(?:tomorrow|tomorrows|tmrw?s?|tmr|more|môre)\b/i;

/** "what date is the tea party", "what day was parents evening": a thing named after it, so a lookup, not today. */
const NAMES_SOMETHING = /\b(?:date|day)\s+(?:is|was|are|were|does|did|will)\s+(?!it\b|today\b|tomorrow\b|tmrw?\b)(?:the\s+|our\s+|my\s+)?[a-z]/i;

export const asksTheDate = (said: string) => ASKS_THE_DATE.test(said) && !NAMES_SOMETHING.test(said);

/** "more = tomorrow", "I meant tomorrow": the same question, for tomorrow. */
export const meansTomorrow = (said: string) => MEANS_TOMORROW.test(said);

export function dateReply(said: string, now: Date = new Date()): string {
  if (meansTomorrow(said)) {
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return `Tomorrow is ${dayLabel(tomorrow)}.`;
  }
  return `Today is ${dayLabel(now)}.`;
}

/* Contact details and instructions */

const ASKS_FOR_CONTACTS =
  /\b(?:phone|cell(?:phone)?|mobile|contact)\s*(?:numbers?|details|nos?|nrs?)\b|\bemail\s+address(?:es)?\b|\bhome\s+addresses\b/i;
const ASKS_FOR_INSTRUCTIONS =
  /\b(?:system\s+prompt|hidden\s+instructions|your\s+instructions|developer\s+mode|admin\s+password|(?:ignore|forget)\s+(?:your|all|previous|the)\s+(?:instructions|rules))\b/i;

/** Someone else's PIN or password, where a person lives, or their medical or private records. */
const ASKS_FOR_PRIVATE_RECORDS =
  /\b(?:pins?|passwords?|passcodes?|login\s+(?:codes?|details)|otps?)\s+(?:of|for)\s+(?:the\s+)?(?:other|all|every|another)\b|\b(?:other|another)\s+(?:teachers?|parents?|users?|people|staff)'?s?\s+(?:pins?|passwords?|logins?)\b|\b(?:home|house|residential)\s+address\b|\bwhere\s+(?:does|do)\s+(?:the\s+)?\w+\s+live\b|\b(?:medical|health|hospital|doctor'?s|disciplinary|counselling|psych(?:ological)?)\s+(?:records?|history|notes?|files?|reports?)\b|\bid\s+numbers?\b/i;

export const PRIVATE_RECORDS_REPLY =
  "I can't share private details like PINs, passwords, home addresses or medical records, and none of that is shown in this chat. Ask the person directly, or speak to your school office.";

export const PRIVATE_DATA_REPLY =
  "I can't share people's phone numbers or contact details in this chat. An Owner or Admin can see one person's details on Users.";
export const INSTRUCTIONS_REPLY = "I can't share my instructions or any passwords. Ask me about Noticeboard instead.";

/** The plain no for contact details or Ask's instructions, if that's what was asked for. */
export function privateDataReply(said: string): string | undefined {
  if (ASKS_FOR_INSTRUCTIONS.test(said)) return INSTRUCTIONS_REPLY;
  const credentialRequest = /\b(?:pins?|passwords?|passcodes?|otps?)\b/i.test(said)
    && /\b(?:what(?:'s|\s+is)|tell|show|give|send|share|reveal|provide|have|get|list)\b/i.test(said)
    && !/\b(?:how|change|reset|forgot|forgotten|recover|update|choose|set)\b/i.test(said);
  if (credentialRequest) return PRIVATE_RECORDS_REPLY;
  if (ASKS_FOR_PRIVATE_RECORDS.test(said)) return PRIVATE_RECORDS_REPLY;
  if (/\b(?:which\s+(?:parents|users|families)|who|list\s+(?:the\s+)?(?:parents|users|families))\b[^.?!]*\b(?:medical|health)\s+conditions?\b/i.test(said)) return PRIVATE_RECORDS_REPLY;
  if (ASKS_FOR_CONTACTS.test(said) && !/\b(?:my|our)\s+(?:own\s+)?(?:phone|contact|number|email)/i.test(said)) return PRIVATE_DATA_REPLY;
  return undefined;
}

/* Who Everyone reaches */

const NAMES_EVERYONE = /\b(?:every\s?one|everybody|evry1|every1|whole school|every parent|all (?:the )?parents)\b/i;
const ASKS_WHO = /\b(?:who|does|do|will|is|if)\b|\?/i;
const ABOUT_REACH = /\b(?:gets?|reach(?:es)?|means?|go(?:es)?\s+to|receives?|choose|spam)\b|\bis\s+every\s?one\s+(?:every|all)\b/i;

export const EVERYONE_REACH_REPLY =
  "Everyone is your whole Organization: every parent and staff member who has joined. To reach only some parents, choose their Circle instead. Nothing is sent until you press Publish.";

/** "if I send to Everyone does every parent get it?", "who gets a notice if I choose Everyone?" */
export function asksWhoEveryoneReaches(said: string): boolean {
  const text = withoutTextSpeak(said);
  return NAMES_EVERYONE.test(text) && ASKS_WHO.test(text) && ABOUT_REACH.test(text) && !/^(?:how|where)\b/i.test(text.trim());
}

/* Whether it went out */

/** "did my civvies notis go out?", "did my image notice go to testing?", "what did we send to testing today?" */
const ASKS_IF_SENT =
  /\b(?:did|has|have)\s+(?:my|our|the|that|this)\b[^?]*\b(?:go(?:ne)?\s+(?:out|to)|went\s+(?:out|to)|been\s+sent|get\s+sent|sent|publish(?:ed)?|actually\s+go)\b|\bwhat\s+went\s+out\b/i;
/** Asked about something else that's sent: an Award's certificates, a count, someone else's Notices. */
const ABOUT_OTHER_SENDING = /\b(?:certificates?|awards?|how\s+many|count|total)\b/i;

export const asksIfSent = (said: string) => {
  const text = withoutTextSpeak(said);
  return ASKS_IF_SENT.test(text) && !ABOUT_OTHER_SENDING.test(text) && !/\bgo\s+back\b/i.test(text);
};

/** Words in the question that aren't about which Notice: "did my image notice actually go out today?" */
const NOT_A_TITLE_WORD = new Set(
  Array.from(
    significantWords(
      "did has have my our the notice notices event events poll polls image picture go gone went sent send out published actually today yesterday this morning what we to testing and also it"
    )
  )
);

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const timeOf = (iso: string) => {
  const at = new Date(iso);
  return timeLabel(`${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`);
};

/** How many sent Notices one answer lists. */
const LISTED = 3;

/**
 * Whether what they asked about went out: the sent Notices whose titles share a word with the
 * question ("did my civvies notis go out?"), or else everything sent today; with who each went to
 * and when, as the Sent tab shows it.
 */
export async function sentStatusReply(said: string, ctx: AnswerContext, now: Date = new Date()) {
  const notices = getSidebarItem("notices");
  const link = { label: `Open ${notices.label}`, href: notices.path };
  const { sent, scheduled } = await loadFeed(ctx);
  const circles = await loadCircles(ctx).catch(() => []);
  const words = Array.from(significantWords(withoutTextSpeak(said))).filter((word) => !NOT_A_TITLE_WORD.has(word));
  const named = (notice: NoticeWithType) => {
    const title = significantWords(notice.title ?? "");
    return words.some((word) => title.has(word));
  };
  const found = words.length > 0 ? sent.filter(named) : sent.filter((notice) => sameDay(new Date(notice.createdAt), now));
  const waiting = words.length > 0 ? scheduled.filter(named) : [];
  const audience = (notice: NoticeWithType) => {
    const circle = circles.find((c) => c.circleId === notice.targetSubcircle);
    return circle ? ` to ${circle.name}` : notice.targetSubcircle ? "" : " to Everyone";
  };
  const line = (notice: NoticeWithType) => {
    const when = sameDay(new Date(notice.createdAt), now) ? `at ${timeOf(notice.createdAt)} today` : `on ${dayLabel(new Date(notice.createdAt))}`;
    return `“${notice.title}” went${audience(notice)} ${when}`;
  };
  let text: string;
  if (found.length > 0) {
    text = `Yes. ${found.slice(0, LISTED).map(line).join("; ")}.`;
  } else if (waiting.length > 0) {
    text = `Not yet: “${waiting[0].title}” is scheduled, so it goes out at its scheduled time. It's under Scheduled on Notices.`;
  } else {
    text =
      words.length > 0
        ? "I can't see a sent Notice like that. If you pressed Publish, it's under Sent on Notices; one still open on Create hasn't gone out."
        : "Nothing has been sent today yet. If you pressed Publish, it's under Sent on Notices.";
  }
  return { text, link };
}
